import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusPill } from '../components/ui/status-pill';

interface DashboardStats {
  advertiserCount: number;
  campaignCountByStatus: Record<string, number>;
  suppressionFileCount: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Compute dashboard stats client-side from real endpoints
    const loadStats = async () => {
      try {
        // Get advertiser count
        const advertisers = await api.getAdvertisers();

        // Get campaigns and count by status
        const campaigns = await api.getCampaigns();
        const campaignCountByStatus = campaigns.reduce(
          (acc, campaign) => {
            const status = campaign.status || 'unknown';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>
        );

        // Count suppression files (campaigns with suppressionFileId not null)
        const suppressionFileCount = campaigns.filter(
          (campaign) => campaign.suppression?.suppressionFileId !== null
        ).length;

        setStats({
          advertiserCount: advertisers.length,
          campaignCountByStatus,
          suppressionFileCount,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Unknown error occurred');
        }
      }
    };

    loadStats();
  }, []);

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading dashboard: {error}
        </div>
      </div>
    );
  }

  const totalCampaigns = stats
    ? Object.values(stats.campaignCountByStatus).reduce((sum, count) => sum + count, 0)
    : 0;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Overview of your lead distribution platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Advertisers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.advertiserCount ?? 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Active advertisers</p>
            <Link to="/advertisers" className="mt-4 block">
              <Button variant="outline" size="sm" className="w-full">
                View Advertisers
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalCampaigns}</div>
            <p className="text-sm text-muted-foreground mt-2">Total campaigns configured</p>
            <Link to="/campaigns" className="mt-4 block">
              <Button variant="outline" size="sm" className="w-full">
                View Campaigns
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Suppression Files</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.suppressionFileCount ?? 0}</div>
            <p className="text-sm text-muted-foreground mt-2">Uploaded suppression files</p>
            <Link to="/suppression" className="mt-4 block">
              <Button variant="outline" size="sm" className="w-full">
                Manage Suppression
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {stats && Object.keys(stats.campaignCountByStatus).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Campaigns by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.campaignCountByStatus).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <StatusPill value={status} />
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
